﻿import type { DeviceTemplate } from '@/api/template.api'

import type {
  AdditionalSectionElement,
  TabColumn,
  TabElement,
  TabRow,
  TabsBlockElement,
  TemplateElement,
  TemplatePin,
  TypedTemplateConfig,
} from '../types'
import { PREVIEW_SERIES_SEED, type RealtimeSeries } from '../utils/previewSnapshot'

/** Surowa zawartość kolumny `templateConfig`. Backend trzyma ją jako `Json?`
 * i nie waliduje kształtu, więc każdy odczyt przechodzi przez `asTypedConfig`. */
export type RawTemplateConfig = Record<string, unknown>

const EMPTY_CONFIG: TypedTemplateConfig = {
  craneControls: [],
  hookControls: [],
  resourceItems: [],
  tabsBlocks: [],
  additionalSections: [],
  pins: [],
}

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : [])

/** Migracja wsteczna elementu: dawny `GaugeCard` na zunifikowany `GaugeControl`
 * w wariancie półokrągłym. Bez tego stary zapis nie miałby już renderera. */
const migrateElement = (element: TemplateElement): TemplateElement => {
  const raw = element as { id: string; type: string; props?: Record<string, unknown> }
  if (raw.type === 'GaugeCard') {
    return { id: raw.id, type: 'GaugeControl', props: { type: 'semicircle', ...(raw.props ?? {}) } } as TemplateElement
  }
  if (raw.type === 'ProgressBar') {
    return { id: raw.id, type: 'ProgressBar', props: { ...(raw.props ?? {}), connector: '.' } } as TemplateElement
  }
  return element
}

/** `CraneSwitchCard` pozostaje w kontrakcie, ale nie jest już konfigurowalnym
 * elementem układu — stały wskaźnik zasilania nie może dostać edytowalnego
 * duplikatu, więc stare wpisy są pomijane we wszystkich strefach. */
const migrateItems = (items: TemplateElement[]): TemplateElement[] =>
  items.filter((element) => element.type !== 'CraneSwitchCard').map(migrateElement)

const normalizeColumn = (column: TabColumn): TabColumn => ({
  columnId: column.columnId,
  items: migrateItems(asArray<TemplateElement>(column.items)),
})

/** Normalizacja zakładki: stary kształt z płaskimi `columns` staje się jednym
 * wierszem, nowy kształt z `rows` zostaje bez zmian. Identyfikatory wierszy są
 * deterministyczne — brakujące wyliczamy z pozycji, żeby sygnatura configu była
 * stabilna między odczytami. */
const normalizeTab = (tab: TabElement): TabElement => {
  const raw = tab as { section?: string; title?: string; rows?: unknown; columns?: unknown }
  const section = typeof raw.section === 'string' ? raw.section : ''
  const title = typeof raw.title === 'string' ? raw.title : ''
  const rawRows = asArray<Partial<TabRow>>(raw.rows)
  if (rawRows.length > 0) {
    return {
      section,
      title,
      rows: rawRows.map((row, i) => ({
        rowId: typeof row.rowId === 'string' && row.rowId ? row.rowId : `${section}-r${i + 1}`,
        columns: asArray<TabColumn>(row.columns).map(normalizeColumn),
      })),
    }
  }
  const legacyColumns = asArray<TabColumn>(raw.columns)
  return {
    section,
    title,
    rows: legacyColumns.length > 0 ? [{ rowId: `${section}-r1`, columns: legacyColumns.map(normalizeColumn) }] : [],
  }
}

const clampPct = (value: number): number => Math.min(100, Math.max(0, value))

/** Pinezki: przechodzą tylko poprawne wpisy o liczbowych współrzędnych 0–100
 * i istniejącej sekcji docelowej. Stare configi bez pola `pins` dostają pustą
 * tablicę, a pinezka wskazująca usuniętą sekcję znika. */
const sanitizePins = (raw: unknown, sections: AdditionalSectionElement[]): TemplatePin[] =>
  asArray<Partial<TemplatePin>>(raw)
    .filter(
      (pin): pin is TemplatePin =>
        typeof pin?.id === 'string' &&
        typeof pin.x === 'number' &&
        Number.isFinite(pin.x) &&
        typeof pin.y === 'number' &&
        Number.isFinite(pin.y) &&
        typeof pin.targetSectionId === 'string' &&
        sections.some((section) => section.id === pin.targetSectionId),
    )
    .map((pin) => ({ ...pin, x: clampPct(pin.x), y: clampPct(pin.y) }))

/** Normalizacja bloków zakładek z migracją wsteczną: stary pojedynczy
 * `tabsElement` wraz z opcjonalnym `tabsBlockIndex` staje się jednym blokiem. */
const normalizeTabsBlocks = (
  rawBlocks: unknown,
  legacy: Record<string, unknown>,
  maxSectionIndex: number,
): TabsBlockElement[] => {
  const blocks = asArray<Partial<TabsBlockElement>>(rawBlocks).filter(
    (b): b is TabsBlockElement => typeof b?.id === 'string' && Array.isArray(b.tabs),
  )
  if (blocks.length > 0) {
    return blocks.map((b) => ({
      id: b.id,
      index: typeof b.index === 'number' ? b.index : 0,
      tabs: b.tabs,
      /* Znacznik nowego rzędu musi przetrwać zapis i odczyt, inaczej blok
         przestawiony w układzie 2D wracałby obok sąsiada. */
      ...(b.rowBreak === true ? { rowBreak: true as const } : {}),
    }))
  }
  const legacyTabs = asArray<TabElement>(legacy.tabsElement)
  if (legacyTabs.length === 0) return []
  const index = typeof legacy.tabsBlockIndex === 'number' ? legacy.tabsBlockIndex : maxSectionIndex + 1
  return [{ id: 'tb-legacy', index, tabs: legacyTabs }]
}

/** Bezpieczna normalizacja `templateConfig` do pełnego kształtu: brakujące
 * sekcje stają się pustymi tablicami, a starsze zapisy są migrowane w locie. */
export const asTypedConfig = (raw: RawTemplateConfig | undefined | null): TypedTemplateConfig => {
  if (!raw) return EMPTY_CONFIG
  const cfg = raw as Partial<TypedTemplateConfig>
  /* Pola starego modelu (`tabsElement`, `tabsBlockIndex`) nie istnieją już
     w typie — czytamy je z luźnego widoku wyłącznie do migracji wstecznej. */
  const legacy = raw as Record<string, unknown>
  const additionalSections = asArray<AdditionalSectionElement>(cfg.additionalSections)
  const maxSectionIndex = additionalSections.reduce(
    (max, s) => (typeof s.index === 'number' && s.index > max ? s.index : max),
    0,
  )
  return {
    craneControls: migrateItems(asArray(cfg.craneControls)),
    hookControls: migrateItems(asArray(cfg.hookControls)),
    resourceItems: migrateItems(asArray(cfg.resourceItems)),
    tabsBlocks: normalizeTabsBlocks(cfg.tabsBlocks, legacy, maxSectionIndex).map((block) => ({
      ...block,
      tabs: asArray<TabElement>(block.tabs).map(normalizeTab),
    })),
    additionalSections: additionalSections.map((section) => ({
      ...section,
      sections: (section.sections ?? []).map((column) => ({ ...column, items: migrateItems(column.items ?? []) })),
    })),
    pins: sanitizePins(cfg.pins, additionalSections),
    img: typeof cfg.img === 'string' && cfg.img ? cfg.img : undefined,
  }
}

/** Deterministyczna serializacja: klucze obiektów sortowane rekurencyjnie,
 * wartości `undefined` pomijane jak w `JSON.stringify`. */
const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null'
  if (Array.isArray(value)) return `[${value.map((item) => stableStringify(item ?? null)).join(',')}]`
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`)
  return `{${entries.join(',')}}`
}

/** Sygnatura treści configu. Obie porównywane strony przechodzą przez
 * `asTypedConfig`, a serializacja jest niewrażliwa na kolejność kluczy —
 * bez tego edytor po zapisie wiecznie raportowałby niezapisane zmiany, bo
 * operacje kanwy dopisują nowe pola na końcu obiektu, a odczyt oddaje config
 * w kanonicznej kolejności. */
export const configSignature = (raw: RawTemplateConfig | TypedTemplateConfig | undefined | null): string =>
  stableStringify(asTypedConfig(raw as RawTemplateConfig | undefined))

/** Wszystkie elementy konfiguracji jednym strumieniem. */
export const collectElements = (config: TypedTemplateConfig): TemplateElement[] => [
  ...config.craneControls,
  ...config.hookControls,
  ...config.resourceItems,
  ...config.tabsBlocks.flatMap((block) =>
    block.tabs.flatMap((tab) => tab.rows.flatMap((row) => row.columns.flatMap((column) => column.items))),
  ),
  ...config.additionalSections.flatMap((section) => section.sections.flatMap((column) => column.items)),
]

export const configHasElements = (config: TypedTemplateConfig): boolean => collectElements(config).length > 0

type SeriesAccumulator = {
  count: number
  /** Źródło bitowe (lampki, kierunki sterowania) wymaga serii całkowitych. */
  bit: boolean
  mins: number[]
  maxs: number[]
}

/** Zbiera pary (dataType, index) z elementów configu i buduje serie podglądu.
 * Znane typy danych dziedziczą bazę z `PREVIEW_SERIES_SEED`, pozostałe dostają
 * zakres wyliczony z `minValue`/`maxValue` elementu. */
export const seriesForConfig = (
  config: TypedTemplateConfig,
  template?: Pick<DeviceTemplate, 'resourceDataType' | 'resourceIndex' | 'deviceStatusDataType' | 'deviceStatusIndex'>,
): RealtimeSeries[] => {
  const acc = new Map<string, SeriesAccumulator>()

  const touch = (
    dataType: string | undefined,
    index: number | null | undefined,
    opts: { bit?: boolean; min?: number; max?: number } = {},
  ): void => {
    if (!dataType) return
    const entry = acc.get(dataType) ?? { count: 0, bit: false, mins: [], maxs: [] }
    entry.count = Math.max(entry.count, (index ?? 0) + 1)
    entry.bit = entry.bit || opts.bit === true
    if (typeof opts.min === 'number') entry.mins.push(opts.min)
    if (typeof opts.max === 'number') entry.maxs.push(opts.max)
    acc.set(dataType, entry)
  }

  for (const element of collectElements(config)) {
    switch (element.type) {
      case 'CraneControlCard':
        touch(
          element.props.arrowLeftDataType ?? element.props.dataType,
          element.props.arrowLeftIndex ?? element.props.index,
          {
            bit: true,
          },
        )
        touch(
          element.props.arrowRightDataType ?? element.props.dataType,
          element.props.arrowRightIndex ?? element.props.index,
          { bit: true },
        )
        break
      case 'CraneSwitchCard':
        touch(element.props.dataType, element.props.index, { bit: true })
        break
      case 'HookControlCard':
        touch(
          element.props.arrowUpDataType ?? element.props.dataType,
          element.props.arrowUpIndex ?? element.props.index,
          {
            bit: true,
          },
        )
        touch(
          element.props.arrowDownDataType ?? element.props.dataType,
          element.props.arrowDownIndex ?? element.props.index,
          { bit: true },
        )
        touch(element.props.arrowLeftDataType, element.props.arrowLeftIndex, { bit: true })
        touch(element.props.arrowRightDataType, element.props.arrowRightIndex, { bit: true })
        touch(element.props.valueDataType, element.props.valueIndex)
        break
      case 'ProgressBar':
        touch(element.props.dataType, element.props.index, {
          min: element.props.minValue,
          max: element.props.maxValue,
        })
        touch(element.props.dataType, element.props.index2)
        break
      case 'GaugeControl':
        touch(element.props.dataType, element.props.index, {
          min: element.props.minValue,
          max: element.props.maxValue,
        })
        break
      case 'StatusIndicator':
        touch(element.props.dataType, element.props.index, { bit: true })
        break
      case 'SmallControlCard':
        touch(element.props.dataType, element.props.index)
        touch(element.props.dataType, element.props.index1)
        touch(element.props.dataType, element.props.index2)
        touch(element.props.dataType, element.props.secondaryIndex)
        break
      case 'InfoTabsStatsCard':
        for (const index of typeof element.props.index === 'string'
          ? element.props.index
              .split(',')
              .map((value) => Number(value.trim()))
              .filter((value) => Number.isSafeInteger(value) && value >= 0)
          : element.props.indexes?.length
          ? element.props.indexes
          : [element.props.index]) {
          touch(element.props.dataType, index)
        }
        break
      case 'VerticalProgressControl':
        for (const chart of element.props.charts) {
          touch(chart.dataType, chart.index, { min: chart.minValue, max: chart.maxValue })
        }
        break
    }
  }

  touch(template?.resourceDataType, template?.resourceIndex, { min: 0, max: 100 })
  touch(template?.deviceStatusDataType, template?.deviceStatusIndex, { bit: true })

  return Array.from(acc.entries()).map(([dataType, entry]) => {
    const seed = PREVIEW_SERIES_SEED.find((s) => s.dataType === dataType)
    const count = Math.max(entry.count, seed?.count ?? 0)
    if (entry.bit) {
      return { dataType, count, base: seed?.base ?? 21, spread: seed?.spread ?? 20, int: true }
    }
    if (entry.mins.length > 0 || entry.maxs.length > 0) {
      const min = entry.mins.length ? Math.min(...entry.mins) : 0
      const max = entry.maxs.length ? Math.max(...entry.maxs) : min + 100
      const span = Math.max(1, max - min)
      return {
        dataType,
        count,
        base: seed?.base ?? min + span * 0.55,
        spread: seed?.spread ?? span * 0.2,
        int: seed?.int,
      }
    }
    return { dataType, count, base: seed?.base ?? 50, spread: seed?.spread ?? 12, int: seed?.int }
  })
}
